// src/admin/AdminHostsDashboard.jsx
// Admin: Hosts dashboard (React SPA) — HOSTS-FIRST
//
// What this version fixes:
// ✅ Shows ALL hosts (including 0 listings)
// ✅ Lazy-loads listings per host on expand
// ✅ Caches listings per host (no repeated reads)
// ✅ Company badge included
// ✅ Avoids subscribing to 10k listings globally
//
// Assumptions / adjust to your schema:
// 1) users collection doc id == auth uid.
// 2) A host marker exists on the user doc.
//    Recommended options (pick the one you actually store):
//      - roles: ["host", ...]            -> roles array-contains "host"
//      - isHost: true                   -> isHost == true
//      - accountType: "host"            -> accountType == "host"
//
// 3) listings collection includes:
//      ownerUid, ownerEmail, title, status, address, city, stateCode,
//      removedAt, submittedAt, publishedAt, createdAt, updatedAt,
//      bookingMode, entranceType, spaceType, capacity
//
// Optional future enhancement:
// - If you maintain hostStats on user docs, this UI will use it automatically.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";

/* ------------------------------ Small UI bits ------------------------------ */

function Pill({ tone = "neutral", children }) {
  const tones = {
    neutral: "border-neutral-200 bg-white text-neutral-700",
    good: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    bad: "border-red-200 bg-red-50 text-red-800",
    info: "border-blue-200 bg-blue-50 text-blue-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

function IconCopy({ className = "h-4 w-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <rect x="2" y="2" width="13" height="13" rx="2" />
    </svg>
  );
}

function IconUsers({ className = "h-5 w-5" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="3" />
      <circle cx="16" cy="8" r="3" />
      <path d="M2 20c0-3.3 2.7-6 6-6" />
      <path d="M22 20c0-3.3-2.7-6-6-6" />
    </svg>
  );
}

function IconSearch({ className = "h-4 w-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

function IconChevronRight({ className = "h-4 w-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function IconChevronDown({ className = "h-4 w-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/* ------------------------------ Main page ------------------------------ */

export default function AdminHostsDashboard() {
  const db = getFirestore();

  // Hosts list (from users)
  const [hostsRaw, setHostsRaw] = useState([]);
  const [loadingHosts, setLoadingHosts] = useState(true);
  const [error, setError] = useState("");

  // Listings cache per host
  const [listingsByUid, setListingsByUid] = useState({});
  const [listingsLoadingByUid, setListingsLoadingByUid] = useState({});
  const [listingsErrorByUid, setListingsErrorByUid] = useState({});

  // UI state
  const [expanded, setExpanded] = useState({});
  const [qText, setQText] = useState("");
  const [sortBy, setSortBy] = useState("recent_desc"); // recent_desc | name_asc | listings_desc

  /* -------------------- 1) Subscribe to ALL hosts -------------------- */
  useEffect(() => {
    setError("");
    setLoadingHosts(true);

    // ✅ Adjust this to your actual host marker.
    // Preferred: roles array-contains "host"
    const qHosts = query(
      collection(db, "users"),
      where("roles", "array-contains", "host")
    );

    const unsub = onSnapshot(
      qHosts,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ uid: d.id, ...d.data() }));
        setHostsRaw(rows);
        setLoadingHosts(false);
      },
      async (err) => {
        // Fallback strategy if roles field doesn't exist in your schema.
        // We'll attempt a second common pattern: isHost == true.
        console.warn("roles host query failed, trying isHost fallback", err);

        try {
          const qFallback = query(
            collection(db, "users"),
            where("isHost", "==", true)
          );

          const unsub2 = onSnapshot(
            qFallback,
            (snap) => {
              const rows = [];
              snap.forEach((d) => rows.push({ uid: d.id, ...d.data() }));
              setHostsRaw(rows);
              setLoadingHosts(false);
            },
            (err2) => {
              console.error(err2);
              setError("Couldn't load hosts. Check your host marker field.");
              setLoadingHosts(false);
            }
          );

          // Replace cleanup with fallback cleanup
          return () => unsub2();
        } catch (e) {
          console.error(e);
          setError("Couldn't load hosts. Check your host marker field.");
          setLoadingHosts(false);
        }
      }
    );

    return () => unsub();
  }, [db]);

  /* -------------------- 2) Normalize host rows -------------------- */
  const hosts = useMemo(() => {
    const rows = (hostsRaw || []).map((u) => {
      const displayName =
        u?.displayName ||
        u?.name ||
        u?.fullName ||
        (u?.firstName || u?.lastName
          ? `${u.firstName || ""} ${u.lastName || ""}`.trim()
          : "") ||
        "—";

      const email = u?.email || u?.contactEmail || "—";
      const phone = u?.phoneE164 || u?.phone || u?.contactPhone || "—";
      const company = u?.company || null;

      // Optional stats (if you add later)
      const hostStats = u?.hostStats || {};
      const activeListingCount = Number.isFinite(hostStats.activeListingCount)
        ? hostStats.activeListingCount
        : null;

      const lastListingActivityAt =
        hostStats.lastListingActivityAt?.toDate?.() ||
        hostStats.lastListingActivityAt ||
        null;

      return {
        ownerUid: String(u.uid),
        user: u,
        displayName,
        email,
        phone,
        company,
        activeListingCount,
        lastListingActivityAt,
      };
    });

    // Sort
    rows.sort((a, b) => {
      if (sortBy === "name_asc") {
        return String(a.displayName).localeCompare(String(b.displayName));
      }
      if (sortBy === "listings_desc") {
        const ac =
          a.activeListingCount ??
          (listingsByUid[a.ownerUid]?.activeCount ?? 0);
        const bc =
          b.activeListingCount ??
          (listingsByUid[b.ownerUid]?.activeCount ?? 0);
        return bc - ac;
      }
      // default recent_desc
      const ad =
        a.lastListingActivityAt?.getTime?.() ||
        listingsByUid[a.ownerUid]?.lastActivityTs ||
        0;
      const bd =
        b.lastListingActivityAt?.getTime?.() ||
        listingsByUid[b.ownerUid]?.lastActivityTs ||
        0;
      return bd - ad;
    });

    return rows;
  }, [hostsRaw, sortBy, listingsByUid]);

  /* -------------------- 3) Search filter -------------------- */
  const filteredHosts = useMemo(() => {
    const q = qText.trim().toLowerCase();
    if (!q) return hosts;

    return hosts.filter((h) => {
      const hay = [
        h.displayName,
        h.email,
        h.phone,
        h.company || "",
        h.ownerUid,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [hosts, qText]);

  /* -------------------- 4) Lazy-load listings per host -------------------- */
  const loadListingsForHost = useCallback(
    async (uid) => {
      if (!uid) return;

      // Already cached
      if (listingsByUid[uid]?.items) return;

      setListingsLoadingByUid((p) => ({ ...p, [uid]: true }));
      setListingsErrorByUid((p) => ({ ...p, [uid]: "" }));

      try {
        // Attempt ordered query first (may require index).
        const baseCol = collection(db, "listings");
        let q1 = query(
          baseCol,
          where("ownerUid", "==", uid),
          orderBy("updatedAt", "desc"),
          limit(50)
        );

        let snap;
        try {
          snap = await getDocs(q1);
        } catch (e) {
          // Fallback without orderBy if updatedAt is missing or index not ready.
          console.warn("ordered listing query failed, fallback", e);
          const q2 = query(
            baseCol,
            where("ownerUid", "==", uid),
            limit(50)
          );
          snap = await getDocs(q2);
        }

        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));

        // Compute lightweight derived stats for UI
        const active = items.filter((l) => l?.removedAt == null);
        const lastActivity = items
          .map((l) => {
            const d =
              l?.updatedAt?.toDate?.() ||
              l?.publishedAt?.toDate?.() ||
              l?.submittedAt?.toDate?.() ||
              l?.createdAt?.toDate?.() ||
              null;
            return d ? d.getTime() : 0;
          })
          .reduce((m, v) => Math.max(m, v), 0);

        setListingsByUid((p) => ({
          ...p,
          [uid]: {
            items,
            activeCount: active.length,
            totalCount: items.length,
            lastActivityTs: lastActivity || 0,
          },
        }));
      } catch (e) {
        console.error(e);
        setListingsErrorByUid((p) => ({
          ...p,
          [uid]: "Couldn't load listings for this host.",
        }));
      } finally {
        setListingsLoadingByUid((p) => ({ ...p, [uid]: false }));
      }
    },
    [db, listingsByUid]
  );

  const toggleExpand = useCallback(
    async (uid) => {
      setExpanded((p) => {
        const next = { ...p, [uid]: !p[uid] };
        return next;
      });

      // If expanding and no cache, load
      const isExpanding = !expanded[uid];
      if (isExpanding && !listingsByUid[uid]?.items) {
        await loadListingsForHost(uid);
      }
    },
    [expanded, listingsByUid, loadListingsForHost]
  );

  /* -------------------- 5) Helpers -------------------- */
  const copy = useCallback(async (txt) => {
    try {
      if (!txt || txt === "—") return;
      await navigator.clipboard.writeText(String(txt));
    } catch {}
  }, []);

  const formatDate = (d) => {
    try {
      if (!d) return "—";
      return new Date(d).toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const statusTone = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "published") return "good";
    if (v.includes("reject")) return "bad";
    if (v.includes("review") || v.includes("submitted")) return "warn";
    if (v === "draft") return "info";
    return "neutral";
  };

  /* ------------------------------ UI ------------------------------ */

  const totalCachedListings = Object.values(listingsByUid || {}).reduce(
    (n, v) => n + (v?.totalCount || 0),
    0
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-50">
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        {/* Header */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 shadow-sm">
              <IconUsers />
              Admin
            </div>
            <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Hosts
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-neutral-600 sm:text-base">
              A unified, read-only view of all host profiles. Expand a host to
              load and review their listings — without accessing host accounts.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatCard
              label="Hosts"
              value={loadingHosts ? "…" : String(filteredHosts.length)}
            />
            <StatCard
              label="Listings (loaded)"
              value={String(totalCachedListings)}
            />
            <StatCard
              label="Profiles live"
              value={loadingHosts ? "Loading…" : "Yes"}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="mt-10 rounded-3xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-md">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                <IconSearch />
              </span>
              <input
                value={qText}
                onChange={(e) => setQText(e.target.value)}
                placeholder="Search host, email, phone, company…"
                className="w-full rounded-2xl border border-neutral-200 bg-neutral-50 px-10 py-3 text-sm font-medium outline-none transition focus:border-neutral-300 focus:bg-white"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs font-semibold text-neutral-500">
                Sort
              </label>
              <select
                className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="recent_desc">Most recent activity</option>
                <option value="listings_desc">Most listings (loaded/stats)</option>
                <option value="name_asc">Name A → Z</option>
              </select>

              <Pill tone={loadingHosts ? "info" : "good"}>
                {loadingHosts ? "Loading hosts…" : "Hosts live"}
              </Pill>
              <Pill tone="neutral">
                Listings load on expand
              </Pill>
            </div>
          </div>

          {!!error && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        {/* Hosts list */}
        <div className="mt-8 grid gap-4">
          {loadingHosts && hostsRaw.length === 0 ? (
            <SkeletonList />
          ) : filteredHosts.length === 0 ? (
            <EmptyState />
          ) : (
            filteredHosts.map((h) => (
              <HostRow
                key={h.ownerUid}
                host={h}
                expanded={!!expanded[h.ownerUid]}
                listingsPack={listingsByUid[h.ownerUid]}
                listingsLoading={!!listingsLoadingByUid[h.ownerUid]}
                listingsError={listingsErrorByUid[h.ownerUid]}
                onToggle={() => toggleExpand(h.ownerUid)}
                onLoad={() => loadListingsForHost(h.ownerUid)}
                copy={copy}
                formatDate={formatDate}
                statusTone={statusTone}
              />
            ))
          )}
        </div>

        {/* Footer note */}
        <div className="mt-10 rounded-2xl border border-neutral-200 bg-white px-4 py-4 text-xs text-neutral-600">
          <strong>Architecture note:</strong> This page is{" "}
          <em>hosts-first</em>. It reads host profiles from{" "}
          <code className="rounded bg-neutral-100 px-1 py-0.5">users</code>{" "}
          and only queries{" "}
          <code className="rounded bg-neutral-100 px-1 py-0.5">listings</code>{" "}
          for a specific host when you expand them. This keeps reads and UI
          load predictable even as listings scale.
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Subcomponents ------------------------------ */

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-xl font-extrabold tracking-tight text-neutral-900">
        {value}
      </div>
    </div>
  );
}

function HostRow({
  host,
  expanded,
  listingsPack,
  listingsLoading,
  listingsError,
  onToggle,
  onLoad,
  copy,
  formatDate,
  statusTone,
}) {
  const {
    ownerUid,
    displayName,
    email,
    phone,
    company,
    activeListingCount,
    lastListingActivityAt,
  } = host;

  const firstName =
    displayName && displayName !== "—"
      ? String(displayName).split(" ")[0]
      : "Host";

  const cachedActive = listingsPack?.activeCount ?? 0;
  const cachedTotal = listingsPack?.totalCount ?? 0;

  const listingCountDisplay =
    activeListingCount != null ? activeListingCount : cachedActive;

  const lastActivityDisplay =
    lastListingActivityAt ||
    (listingsPack?.lastActivityTs
      ? new Date(listingsPack.lastActivityTs)
      : null);

  const topListings = (listingsPack?.items || [])
    .slice()
    .sort((a, b) => {
      const ad =
        a?.updatedAt?.toDate?.()?.getTime?.() ||
        a?.publishedAt?.toDate?.()?.getTime?.() ||
        a?.submittedAt?.toDate?.()?.getTime?.() ||
        a?.createdAt?.toDate?.()?.getTime?.() ||
        0;
      const bd =
        b?.updatedAt?.toDate?.()?.getTime?.() ||
        b?.publishedAt?.toDate?.()?.getTime?.() ||
        b?.submittedAt?.toDate?.()?.getTime?.() ||
        b?.createdAt?.toDate?.()?.getTime?.() ||
        0;
      return bd - ad;
    })
    .slice(0, 4);

  const adminHostHref = `/admin/hosts/${encodeURIComponent(ownerUid)}`;
  const adminListingsHref = `/admin/listings?ownerUid=${encodeURIComponent(
    ownerUid
  )}`;

  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-sm transition hover:shadow-md sm:p-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        {/* Left block */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-lg font-bold text-neutral-900 sm:text-xl">
              {displayName}
            </div>

            {company ? (
              <Pill tone="info">{company}</Pill>
            ) : (
              <Pill tone="neutral">No company set</Pill>
            )}

            <Pill tone="neutral">
              {listingCountDisplay} active listing{listingCountDisplay === 1 ? "" : "s"}
            </Pill>

            <Pill tone="neutral">
              Last activity: {formatDate(lastActivityDisplay)}
            </Pill>

            {cachedTotal > 0 && (
              <Pill tone="neutral">
                {cachedTotal} loaded
              </Pill>
            )}
          </div>

          <div className="mt-3 grid gap-2 text-sm text-neutral-700 sm:grid-cols-2">
            <Field label="Email" value={email} onCopy={() => copy(email)} />
            <Field label="Phone" value={phone} onCopy={() => copy(phone)} />
            <Field
              label="Owner UID"
              value={ownerUid}
              mono
              onCopy={() => copy(ownerUid)}
            />
            <Field
              label="Listings loaded"
              value={
                listingsPack?.items
                  ? String(listingsPack.items.length)
                  : "Not loaded"
              }
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={async () => {
              if (!listingsPack?.items && !listingsLoading) {
                await onLoad();
              }
              onToggle();
            }}
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-xs font-bold text-neutral-800 transition hover:bg-neutral-50"
            title={expanded ? "Hide listings" : "Load & show listings"}
            type="button"
          >
            {expanded ? "Hide listings" : "Show listings"}
            {expanded ? <IconChevronDown /> : <IconChevronRight />}
          </button>

          <a
            href={adminListingsHref}
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-xs font-bold text-neutral-800 transition hover:bg-neutral-50"
            title={`View ${firstName}'s listings in listings page`}
          >
            Listings page
            <IconChevronRight />
          </a>

          <a
            href={adminHostHref}
            className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-xs font-bold text-white transition hover:opacity-90"
            title={`Open ${firstName}'s host profile`}
          >
            Host profile
            <IconChevronRight />
          </a>
        </div>
      </div>

      {/* Expand area */}
      {expanded && (
        <div className="mt-5">
          {listingsLoading && (
            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
              Loading listings…
            </div>
          )}

          {!!listingsError && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {listingsError}
            </div>
          )}

          {!listingsLoading && !listingsError && (!listingsPack?.items || listingsPack.items.length === 0) && (
            <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-6 text-sm text-neutral-600">
              This host doesn’t have any listings yet.
            </div>
          )}

          {!!listingsPack?.items?.length && (
            <>
              {/* Listings preview */}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {topListings.map((l) => (
                  <div
                    key={l.id}
                    className="rounded-2xl border border-neutral-200 bg-gradient-to-br from-white to-neutral-50 p-4"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-neutral-900">
                          {l.title || "Untitled listing"}
                        </div>
                        <div className="mt-1 truncate text-[11px] text-neutral-500">
                          {(l.address || "").trim() || "Address not set"}
                        </div>
                        <div className="mt-1 truncate text-[11px] text-neutral-500">
                          {formatCityState(l.city, l.stateCode)}
                        </div>
                      </div>
                      <Pill tone={statusTone(l.status)}>
                        {String(l.status || "—")}
                      </Pill>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {l.spaceType && <MiniTag>{l.spaceType}</MiniTag>}
                      {l.entranceType && <MiniTag>{l.entranceType}</MiniTag>}
                      {l.bookingMode && <MiniTag>{l.bookingMode}</MiniTag>}
                      {Number.isFinite(l.capacity) && (
                        <MiniTag>{l.capacity} cap</MiniTag>
                      )}
                    </div>
                  </div>
                ))}

                {/* Fillers if fewer than 4 */}
                {topListings.length < 4 &&
                  Array.from({ length: 4 - topListings.length }).map((_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="rounded-2xl border border-dashed border-neutral-200 bg-white p-4 text-center text-xs text-neutral-400"
                    >
                      No additional listing
                    </div>
                  ))}
              </div>

              <div className="mt-4 text-[11px] text-neutral-500">
                Showing up to 4 most recently active listings. Use “Listings page”
                for the full list.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onCopy, mono = false }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-28 text-[10px] font-bold uppercase tracking-wider text-neutral-400">
        {label}
      </div>
      <div
        className={`min-w-0 flex-1 truncate text-sm font-semibold text-neutral-800 ${
          mono ? "font-mono text-[12px]" : ""
        }`}
        title={value || ""}
      >
        {value || "—"}
      </div>
      {onCopy && value && value !== "—" && (
        <button
          onClick={onCopy}
          className="inline-flex items-center justify-center rounded-full border border-neutral-200 bg-white p-1.5 text-neutral-500 transition hover:bg-neutral-50 hover:text-neutral-800"
          title="Copy"
          type="button"
        >
          <IconCopy />
        </button>
      )}
    </div>
  );
}

function MiniTag({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-600">
      {children}
    </span>
  );
}

function SkeletonList() {
  return (
    <div className="grid gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="rounded-3xl border border-neutral-200 bg-white p-6"
        >
          <div className="h-5 w-48 animate-pulse rounded bg-neutral-200" />
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <div className="h-4 w-full animate-pulse rounded bg-neutral-200" />
            <div className="h-4 w-full animate-pulse rounded bg-neutral-200" />
            <div className="h-4 w-full animate-pulse rounded bg-neutral-200" />
            <div className="h-4 w-full animate-pulse rounded bg-neutral-200" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-10 text-center shadow-sm">
      <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs font-semibold text-neutral-600">
        <IconUsers />
        No results
      </div>
      <div className="mt-4 text-xl font-bold text-neutral-900">
        No hosts match your search.
      </div>
      <div className="mt-2 text-sm text-neutral-600">
        Try searching by name, email, phone, or company.
      </div>
    </div>
  );
}

/* ------------------------------ Helpers ------------------------------ */

function formatCityState(city, stateCode) {
  const c = (city || "").trim();
  const s = (stateCode || "").trim();
  if (c && s) return `${c}, ${s}`;
  return c || s || "—";
}
